import { ChevronDown, Menu, X } from "lucide-react";
import { useRouter } from 'next/navigation';
import { Button } from "../ui";
import { useState } from 'react';

interface AuthHeaderProps {
    currentPage?: 'login' | 'signup' | 'login-business' | 'signup-business' | 'reset-password' | 'setup-password';
}

export function AuthHeader({ currentPage }: AuthHeaderProps) {
    const router = useRouter();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <>
            {/* Desktop Header */}
            <div className='hidden backdrop-blur-sm bg-[#eae7e0c1] md:flex py-10 px-5 w-full h-16 justify-between items-center fixed z-50'>
                <div className='flex gap-5'>
                    {/* TODO: add Partners when having clear requirements + add onClick functionality */}
                    <div 
                        className='opacity-70 flex text-[20px] items-center cursor-not-allowed transition-colors'
                        role="link"
                        aria-disabled="true"
                        tabIndex={-1}
                    >
                        Partners <ChevronDown color='#cdc4c1' size={16} aria-hidden="true" />
                    </div>
                    {/* Business Button */}
                    <div 
                        role="button"
                        tabIndex={0}
                        aria-label="Businesses login or sign up"
                        className='flex text-[20px] items-center cursor-pointer hover:text-gray-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md'
                        onClick={() => {
                            if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                router.push('/login-business');
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                    router.push('/login-business');
                                }
                            }
                        }}
                    >
                        Businesses
                        <ChevronDown color='#cdc4c1' size={16} aria-hidden="true" />
                    </div>
                    
                    
                </div>
                <div className='flex gap-7'>
                    <Button
                        type="button"
                        className={`h-8 border-0 w-[45%] rounded-full text-center text-[20px] font-black transition-all duration-200 py-4.5 px-8 ${
                            currentPage === 'login'
                                ? 'opacity-30 cursor-not-allowed bg-[#cdc4c1] hover:bg-[#cdc4c1]'
                                : 'bg-[#cdc4c1] hover:bg-[#b8b0ad] cursor-pointer'
                        }`}
                        onClick={() => router.push('/login')}
                        disabled={currentPage === 'login'}
                    >
                        Login
                    </Button>
                    <Button
                        type="button"
                        className={`h-8 border-0 rounded-full w-[45%] text-center text-[20px] font-black transition-all duration-200 py-4.5 px-8 ${
                            currentPage === 'signup'
                                ? 'opacity-30 cursor-not-allowed bg-[#948780] hover:bg-[#cdc4c1]'
                                : 'bg-[#948780] hover:bg-[#b8b0ad] cursor-pointer'
                        }`}
                        onClick={() => router.push('/signup')}
                        disabled={currentPage === 'signup'}
                    >
                        Sign Up
                    </Button>
                </div>
            </div>

            {/* Mobile Header */}
            <div className='md:hidden backdrop-blur-sm bg-[#eae7e0c1] px-4 w-full h-16 flex justify-between items-center fixed z-50 border-b border-gray-200'>
                <div className='flex gap-3'>
                    {/* Business Button */}
                    <div 
                        role="button"
                        tabIndex={0}
                        aria-label="Businesses login or sign up"
                        className='flex text-[16px] items-center cursor-pointer hover:text-gray-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md'
                        onClick={() => {
                            if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                router.push('/login-business');
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                    router.push('/login-business');
                                }
                            }
                        }}
                    >
                        Businesses
                        <ChevronDown color='#cdc4c1' size={16} aria-hidden="true" />
                    </div>
                    {/* TODO: add Partners when having clear requirements */}
                    {/* <div className='flex text-[16px] items-center cursor-pointer hover:text-gray-600 transition-colors' onClick={handlePartners}>
                        Partners <ChevronDown color='#cdc4c1' size={16} />
                    </div> */}
                </div>
                
                <button
                    onClick={toggleMobileMenu}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isMobileMenuOpen}
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-40 bg-opacity-50" onClick={toggleMobileMenu}>
                    <div className="fixed top-16 right-0 w-64 h-full shadow-lg transform transition-transform duration-300 ease-in-out bg-white">
                        <div className="p-6 space-y-4 h-full">
                            <div className="space-y-3">
                                {/* Business Button */}
                                <div 
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Businesses login or sign up"
                                    className="flex items-center text-[18px] cursor-pointer hover:text-gray-600 transition-colors py-2 text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md"
                                    onClick={() => {
                                        if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                            router.push('/login-business');
                                        }
                                        setIsMobileMenuOpen(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (currentPage !== 'login-business' && currentPage !== 'signup-business') {
                                                router.push('/login-business');
                                            }
                                            setIsMobileMenuOpen(false);
                                        }
                                    }}
                                >
                                    Business
                                </div>
                                
                                {/* TODO: add Partners when having clear requirements */}
                                {/* <div 
                                    className="flex items-center text-[18px] cursor-pointer hover:text-gray-600 transition-colors py-2 text-gray-800"
                                    onClick={handlePartners}
                                >
                                    Partners <ChevronDown color='#cdc4c1' className="ml-2" />
                                </div> */}
                            </div>
                            
                            <div className="pt-4 border-t border-gray-200 space-y-3">
                                <Button
                                    type="button"
                                    className={`w-full h-10 border-0 rounded-full text-center text-[18px] font-black transition-all duration-200 ${
                                        currentPage === 'login'
                                            ? 'opacity-30 cursor-not-allowed bg-[#cdc4c1] hover:bg-[#cdc4c1]'
                                            : 'bg-[#cdc4c1] hover:bg-[#b8b0ad] cursor-pointer'
                                    }`}
                                    onClick={() => { 
                                        router.push('/login'); 
                                        setIsMobileMenuOpen(false); 
                                    }}
                                    disabled={currentPage === 'login'}
                                >
                                    Login
                                </Button>
                                <Button
                                    type="button"
                                    className={`w-full h-10 border-0 rounded-full text-center text-[18px] font-black transition-all duration-200 ${
                                        currentPage === 'signup'
                                            ? 'opacity-30 cursor-not-allowed bg-[#948780] hover:bg-[#cdc4c1]'
                                            : 'bg-[#948780] hover:bg-[#b8b0ad] cursor-pointer'
                                    }`}
                                    onClick={() => { 
                                        router.push('/signup'); 
                                        setIsMobileMenuOpen(false); 
                                    }}
                                    disabled={currentPage === 'signup'}
                                >
                                    Sign Up
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}